import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { formatDate                } from '@angular/common';
import { Observable, of            } from 'rxjs';

import { TableHeaderEntryModel, TableBodyEntryModel, TableBodyCellEntryModel, ParsedFileModel           } from '@shared/models';
import { InvoicesService, NotificationService, InvoicesCountersService, WebsocketService, HelperService } from '@shared/services';
import { Invoice } from '@shared/factories';

@Component({
  selector: 'invoices-table',
  templateUrl: '../../table-entries.component.html',
  styleUrls: ['./invoices-table.component.sass'],
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InvoicesTableComponent {
  @Input() rows:         Invoice[];
  @Input() paginationId: string;
  @Input() loading:      boolean;

  @Input() filter:       string;
  @Output() headerCallback = new EventEmitter<void>();
  constructor(
    private invoicesService:         InvoicesService,
    private invoicesCountersService: InvoicesCountersService,
    private websocketService:        WebsocketService,
    private helperService:           HelperService,
    private notificationService:     NotificationService,
  ) { }

  headerCallbackHandler(res): void {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    return 'invoices-table';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Dokumenttyp',    class: 'doc-type-cell',      sort_by: ['doc_type_id']                                 },
      { title: 'Betreff',        class: 'subject-cell',       sort_by: ['subject']                                     },
      { title: 'Rechnungsdatum', class: 'invoice-date-cell',  sort_by: ['invoice_date']                                },
      { title: 'Archiviert',     class: 'archived-at-cell',   sort_by: ['archived_at'], pageOnly: ['invoices-archive'] },
      { title: 'Archiviert von', class: 'archived-by-cell',                             pageOnly: ['invoices-archive'] },
      { title: 'Aktion',         class: 'action-cell'                                                                  },
    ].filter(h => h.pageOnly ? h.pageOnly.find(p => p === this.filter) : true);
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array(10).fill({
      cells: [
        { xs_label: ' ', class: 'doc-type-cell',     value: ' '                                 },
        { xs_label: ' ', class: 'subject-cell',      value: ' '                                 },
        { xs_label: ' ', class: 'invoice-date-cell', value: ' '                                 },
        { xs_label: ' ', class: 'archived-at-cell',  value: ' ', pageOnly: ['invoices-archive'] },
        { xs_label: ' ', class: 'archived-by-cell',  value: ' ', pageOnly: ['invoices-archive'] },
        {                class: 'action-cell',       buttons: [
          {               class: 'height-36 width-40'          },
          { label: 'PDF', class: 'height-36 color-transparent' },
          { label: 'XML', class: 'height-36 color-transparent' },
          {               class: 'action-cell-button'          }
        ]}
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    });
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    return this.rows.map((i: Invoice): TableBodyEntryModel => ({
      bold: !i.customerReadAt,
      cells: [
        { xs_label: 'Dokumenttyp',    class: 'doc-type-cell word-break-all', value: i.doc_type_name                                                                                       },
        { xs_label: 'Betreff',        class: 'subject-cell word-break-all',  value: i.subject                                                                                             },
        { xs_label: 'Rechnungsdatum', class: 'invoice-date-cell',            value: i.invoice_date ? formatDate(i.invoice_date, 'dd.MM.yyyy', 'de') : '-'                                 },
        { xs_label: 'Archiviert',     class: 'archived-at-cell',             value: i.archived_at  ? formatDate(i.archived_at,  'dd.MM.yyyy', 'de') : '-', pageOnly: ['invoices-archive'] },
        { xs_label: 'Archiviert von', class: 'archived-by-cell',             value: i.archived_by  ? i.archived_by.name                             : '-', pageOnly: ['invoices-archive'] },
        { xs_label: ' ',              class: `action-cell ${this.filter === 'archive' ? 'archive' : ''}`,
          buttons: [
            {
              class: 'am-flex-basis-auto width-40 btn white font-small-icon tooltip-hover',
              icon: `${i.archived_at ? 'icon-rotate-ccw'  : 'icon-archive'} am-flex-center`,
              tooltip: i.archived_at ? 'Wiederherstellen' : 'Archivieren',
              click: () => i.archived_at ? this.manualUnArchive(i) : this.manualArchive(i)
            },
            {
              label: 'PDF',
              class: `btn white ${i.pdfAttached ? '' : 'disabled events-none'}`,
              labelClass: 'am-flex-center font-l tooltip-hover',
              click: () => this.saveInvoice(i, 'pdf'),
              tooltip: 'Speichern'
            },
            {
              label: 'XML',
              class: `btn white ${i.xmlAttached ? '' : 'disabled events-none'}`,
              labelClass: 'am-flex-center font-l tooltip-hover',
              click: () => this.saveInvoice(i, 'xml'),
              tooltip: 'Speichern'
            },
            {
              class: 'action-cell-button',
              label: 'Ansehen',
              click: () => this.openPrint(i)
            }
          ]
        }
      ].filter((c: TableBodyCellEntryModel) => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.filter) : true)
    }));
  }

  private manualArchive(invoice: Invoice): void {
    this.notificationService.confirm(
      'Bestätigung',
      'Möchten Sie dieses Dokument wirklich archivieren?',
      () => {
        this.notificationService.wait();
        this.invoicesService.archiveInvoice(invoice.id).pipe(
          take(1),
          switchMap(res => this.readInvoice(res))
        ).subscribe(
          res => this.refreshInvoices(),
          err => this.notificationService.alert(err)
        );
      }
    );
  }

  private manualUnArchive(invoice: Invoice): void {
    this.notificationService.confirm(
      'Bestätigung',
      'Möchten Sie dieses Dokument wirklich wiederherstellen?',
      () => {
        this.notificationService.wait();
        this.invoicesService.unArchiveInvoice(invoice.id).pipe(
          take(1),
          switchMap(res => this.readInvoice(res))
        ).subscribe(
          res => this.refreshInvoices(),
          err => this.notificationService.alert(err)
        );
      }
    );
  }

  private refreshInvoices(): void {
    if (!this.websocketService.wsOnline()) {
      this.invoicesService.forceReload();
      this.invoicesCountersService.reloadCounters();
    }
  }

  private getInvoiceBlob(invoice: Invoice, type: string): Observable<ParsedFileModel> {
    let mimeType = this.getMimeType(type);
    let url      = this.invoicesService.getDocumnetUrlByType(invoice, type);

    return this.helperService.loadFileAsBlobByUrl(url, mimeType).pipe(
      switchMap(res => this.readInvoice(invoice).pipe(
        map(() => res)
      ))
    );
  }

  private openPrint(invoice: Invoice): void {
    this.notificationService.wait();
    this.getInvoiceBlob(invoice, 'pdf').pipe(tap(res => this.helperService.openPrintPreview(res))).subscribe(
      res => this.notificationService.close(),
      err => this.notificationService.alert(err)
    );
  }

  private saveInvoice(invoice: Invoice, type: string): void {
    this.notificationService.wait();
    this.getInvoiceBlob(invoice, type).pipe(tap(res => this.helperService.downloadFile(res))).subscribe(
      res => this.notificationService.close(),
      err => this.notificationService.alert(err)
    );
  }

  private getMimeType(type: string): string {
    switch (type) {
      case 'pdf': { return 'application/pdf'; };
      case 'xml': { return 'text/xml';        };
      default:    { return null;              };
    }
  }

  private readInvoice(invoice: Invoice): Observable<Invoice> {
    if (!invoice.customerReadAt) return this.invoicesService.readInvoice(invoice.id).pipe(
      tap(res => {
        let index = this.rows.findIndex(r => r.id === invoice.id);
        if (index >= 0) this.rows[index].customerReadAt = res.customerReadAt;
      })
    );
    return of(null);
  }

}
